import { withLoading } from 'app/store/utils';
import { captureException } from '@sentry/react';
import serializeError, { ErrorLike } from 'util/serializeError';
import { graphqlOperation } from 'aws-amplify';
import { fileAccessService } from 'app/services/fileAccess.service';
import { isNotNullOrUndefined, isPaidolUser } from 'util/typeGuards';
import { paidolService, PaidolUserPayload } from 'app/services/paidol.service';
import { userService } from 'app/services/user.service';
import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetGroupPaidolUsers } from 'app/pages/store/usersSliceQueries';
import type { GetGroupPaidolUsersQuery } from 'app/pages/store/usersSliceQueries';
import type { GraphQLResult } from '@aws-amplify/api-graphql';
import { AppDispatch } from 'app/store';
import type { RootState } from 'app/store/rootReducer';
import { PaidolUser, Role } from 'API';
import { createDebouncedThunk } from 'util/thunkHelpers';
import { graphqlRequest } from 'app/services/middleware';
import { DEFAULT_USER_FILTER } from 'app/store/filtering/userFilterConfig';

// Default page size for user queries
export const DEFAULT_USERS_PAGE_SIZE = 2000;

interface GetUsersArgs {
  rowsPerPage: number;
  nextToken: string | null;
  selectedCompany: string;
  prioritizeCurrentUser?: boolean;
  filter?: any; // UserStatusFilterInput structure
  sort?: { field: string; direction: string } | undefined;
}

export const getUsers = createAsyncThunk<Array<PaidolUser> | void, GetUsersArgs, { rejectValue: ErrorLike }>(
  'team/users/getUsers',
  async (
    { rowsPerPage, nextToken = null, selectedCompany, prioritizeCurrentUser = false, filter, sort },
    { rejectWithValue, dispatch }
  ) => {
    // _replacesDefault is set by generateUserFilterObject when status filter is active
    const replacesDefault = filter?._replacesDefault === true;

    const finalFilter = filter
      ? {
          and: replacesDefault
            ? filter.and || []
            : [...(DEFAULT_USER_FILTER.and || []), ...(filter.and || [])],
        }
      : DEFAULT_USER_FILTER;

    const queryInput = {
      input: {
        paidol_id: selectedCompany,
        limit: rowsPerPage,
        nextToken,
        prioritizeCurrentUser,
        filter: finalFilter,
        sort: sort || undefined,
      },
    };

    try {
      const res = (await userService.listPaidolUsersFiltered(queryInput.input)) as {
        items: PaidolUser[];
        nextToken?: string | null;
      };

      const paidolUsers = res?.items?.filter(isNotNullOrUndefined).filter(isPaidolUser) || [];

      dispatch(setNextToken(res?.nextToken || null));
      dispatch(getUsersPictures(paidolUsers));

      return paidolUsers;
    } catch (error) {
      return rejectWithValue(serializeError(error as Error));
    }
  }
);

export interface getAllUsersNoLimitInput {
  selectedCompany: string;
  email?: string;
}

export const getAllUsersNoLimitDebounced = (props: getAllUsersNoLimitInput) =>
  createDebouncedThunk(
    (dispatch: AppDispatch) => {
      return dispatch(getAllUsersNoLimit(props));
    },
    'getAllUsersNoLimit',
    500
  );

export const getAllUsersNoLimit = createAsyncThunk<
  Array<PaidolUser> | void,
  { selectedCompany: string; email?: string },
  { rejectValue: ErrorLike }
>('team/users/getAllUsersNoLimit', async ({ selectedCompany, email }, { rejectWithValue, dispatch }) => {
  try {
    let allUsers: PaidolUser[] = [];
    let nextToken: string | null = null;

    do {
      const response = (await userService.listPaidolUsersFiltered({
        paidol_id: selectedCompany,
        limit: DEFAULT_USERS_PAGE_SIZE,
        nextToken: nextToken || undefined,
        ...(email ? { email } : {}),
      })) as { items: PaidolUser[]; nextToken?: string | null };

      const users = response?.items?.filter(isNotNullOrUndefined).filter(isPaidolUser) || [];
      allUsers = [...allUsers, ...users];
      nextToken = response?.nextToken || null;
    } while (nextToken);

    return allUsers;
  } catch (error) {
    return rejectWithValue(serializeError(error as Error));
  }
});

interface getAdminsArgs {
  selectedCompany: string;
  roleType: 'admin' | 'group' | 'both';
}

export const getAdmins = createAsyncThunk<
  Array<PaidolUser> | void,
  getAdminsArgs,
  { rejectValue: ErrorLike }
>('team/users/getAdmins', async ({ selectedCompany, roleType }, { rejectWithValue, dispatch }) => {
  try {
    let rolesFilter: { roles: { eq: Role } }[] = [];

    if (roleType === 'admin') {
      rolesFilter = [{ roles: { eq: Role.ADMINISTRATOR } }];
    } else if (roleType === 'group') {
      rolesFilter = [{ roles: { eq: Role.GROUP_ADMINISTRATOR } }];
    } else if (roleType === 'both') {
      rolesFilter = [{ roles: { eq: Role.ADMINISTRATOR } }, { roles: { eq: Role.GROUP_ADMINISTRATOR } }];
    }

    let allPaidolUsers: PaidolUser[] = [];
    let nextToken: string | null = null;

    do {
      const response = (await userService.listPaidolUsersFiltered({
        paidol_id: selectedCompany,
        limit: DEFAULT_USERS_PAGE_SIZE,
        nextToken: nextToken || undefined,
        filter: {
          and: [
            {
              or: rolesFilter,
            },
          ],
        },
      })) as { items: PaidolUser[]; nextToken?: string | null };

      const paidolUsers = response?.items?.filter(isNotNullOrUndefined).filter(isPaidolUser) || [];

      allPaidolUsers = [...allPaidolUsers, ...paidolUsers];
      nextToken = response?.nextToken || null;
    } while (nextToken);

    dispatch(setGroupAdmins(allPaidolUsers));
    dispatch(getUsersPictures(allPaidolUsers));

    return allPaidolUsers;
  } catch (error) {
    return rejectWithValue(serializeError(error as Error));
  }
});

export interface GetGroupsUsersArgs {
  paidolId: string;
  nextToken?: string;
  limit?: number;
  userId: string;
  userRole?: Role;
}

export const getGroupUsers = createAsyncThunk<
  Array<PaidolUser> | void,
  GetGroupsUsersArgs,
  { rejectValue: ErrorLike }
>(
  'cards/groups/getGroupUsers',
  async (
    { paidolId, userId, userRole, nextToken, limit }: GetGroupsUsersArgs,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const results = await (graphqlRequest(
        graphqlOperation(GetGroupPaidolUsers, { paidolId, nextToken, limit }),
        dispatch
      ) as Promise<GraphQLResult<GetGroupPaidolUsersQuery>>);

      const groups = results.data?.listCardGroupsByPaidolId?.items || [];

      const filteredGroups = groups.filter(
        (group) =>
          group.paymentCards?.items?.some((card) =>
            card?.paidolUsers?.items?.some((userItem) => userItem?.paidolUser?.user?.id === userId)
          ) || group.groupAdmins?.items?.some((admin) => admin?.paidolUser?.user?.id === userId)
      );

      const paidolUsers = filteredGroups
        ?.flatMap((group) => {
          const admins = group.groupAdmins?.items?.map((adminItem) => {
            const paidolUser = adminItem?.paidolUser;
            const user = paidolUser?.user;
            if (paidolUser?.id && paidolUser?.email && user?.id) {
              return {
                user_id: paidolUser.user_id,
                id: paidolUser.id,
                email: paidolUser.email,
                roles: paidolUser.roles || null,
                user: {
                  id: user.id,
                  first_name: user.first_name || null,
                  last_name: user.last_name || null,
                  picture: user.picture || null,
                  email: user.email || null,
                },
              };
            }
            return null;
          });

          const users = group.paymentCards?.items?.flatMap((card) =>
            card?.paidolUsers?.items?.map((userItem) => {
              const paidolUser = userItem?.paidolUser;
              const user = paidolUser?.user;
              if (paidolUser?.id && paidolUser?.email && user?.id) {
                return {
                  user_id: paidolUser.user_id,
                  id: paidolUser.id,
                  email: paidolUser.email,
                  roles: paidolUser.roles || null,
                  user: {
                    id: user.id,
                    first_name: user.first_name || null,
                    last_name: user.last_name || null,
                    picture: user.picture || null,
                    email: user.email || null,
                  },
                };
              }
              return null;
            })
          );

          return userRole === 'GROUP_ADMINISTRATOR'
            ? [...(admins || []), ...(users || [])]
            : [...(admins || []), ...(users?.filter((user) => user?.roles !== 'GROUP_ADMINISTRATOR') || [])];
        })
        .filter(Boolean) as PaidolUser[];

      const usersResult = await dispatch(
        getUsers({
          selectedCompany: paidolId,
          rowsPerPage: DEFAULT_USERS_PAGE_SIZE,
          nextToken: null,
        })
      ).unwrap();

      const allUsers = Array.isArray(usersResult) ? usersResult : [];

      const userInPaidolUsers = paidolUsers.some((user) => user.user?.id === userId);

      if (!userInPaidolUsers) {
        const fetchedUser = allUsers.find((user) => user.user?.id === userId);
        if (fetchedUser) {
          paidolUsers.unshift(fetchedUser);
        }
      }

      dispatch(getUsersPictures(paidolUsers));

      return paidolUsers;
    } catch (error) {
      captureException(error);
      return rejectWithValue({
        message: 'Failed to fetch group users',
        name: '',
      });
    }
  }
);

export const getUsersPictures = createAsyncThunk(
  'team/users/getUsersPictures',
  async (paidolUsers: Array<PaidolUser>) => {
    return Promise.all(
      paidolUsers.map((paidolUser) => {
        if (paidolUser.user && paidolUser.user.picture) {
          // One unresolvable picture must not blank every avatar in the list.
          return fileAccessService
            .getDownloadUrl(paidolUser.user.picture)
            .then((url) => [paidolUser.user_id, url])
            .catch(() => [paidolUser.user_id, undefined]);
        }
        return [paidolUser.user_id, undefined];
      })
    );
  }
);

export const removeUser = createAsyncThunk(
  'team/users/removeUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      await userService.removeUser({ paidolUserId: userId });
      return userId;
    } catch (error) {
      return rejectWithValue(serializeError(error as Error));
    }
  }
);

interface InviteUserArgs {
  selectedCompany: string;
  email: string;
  roles: string;
}

export const addUser = createAsyncThunk<PaidolUserPayload | null, InviteUserArgs, { rejectValue: ErrorLike }>(
  'team/users/addUser',
  async (params: InviteUserArgs, { rejectWithValue, dispatch }) => {
    const companyUser = {
      paidol_id: params.selectedCompany,
      email: params.email.toLowerCase(),
      roles: params.roles as 'ADMINISTRATOR' | 'GROUP_ADMINISTRATOR' | 'CARDHOLDER' | 'MEMBER',
    };

    let hostname = window.location.origin;
    if (!window.location.origin) {
      hostname =
        window.location.protocol +
        '//' +
        window.location.hostname +
        (window.location.port ? ':' + window.location.port : '');
    }

    try {
      const result = await paidolService.inviteUser(companyUser, hostname);
      return result || rejectWithValue({ message: 'An unknown error occurred' } as ErrorLike);
    } catch (error) {
      return rejectWithValue(serializeError(error as Error));
    }
  }
);

interface VisibilitySettings {
  isAllAvailable: boolean;
  ids: string[];
}

interface UpdateUserArgs {
  id: string;
  roles: Role | null | undefined;
  glCodeFilteringByUser: VisibilitySettings;
  projectFilteringByUser: VisibilitySettings;
  departmentVisibilitySettings?: VisibilitySettings;
}

interface UpdateUserExtraArgs {
  paidolId: string;
}

export const updateUser = createAsyncThunk(
  'team/users/updateUser',
  // SEE-856 D1: routes through the admin-only adminUpdateMember Lambda
  // instead of the auto-gen updatePaidolUser mutation. The Lambda enforces
  // assert_admin, blocks self-promotion, and rejects cross-paidol targets.
  async (params: UpdateUserArgs & UpdateUserExtraArgs, { rejectWithValue }) => {
    const input = {
      paidolUserId: params.id,
      paidolId: params.paidolId,
      roles: params.roles ?? Role.CARDHOLDER,
      glCodeVisibilitySettings: {
        isAllAvailable: params.glCodeFilteringByUser.isAllAvailable,
        ids: params.glCodeFilteringByUser.ids,
      },
      projectVisibilitySettings: {
        isAllAvailable: params.projectFilteringByUser.isAllAvailable,
        ids: params.projectFilteringByUser.ids,
      },
      ...(params.departmentVisibilitySettings && {
        departmentVisibilitySettings: {
          isAllAvailable: params.departmentVisibilitySettings.isAllAvailable,
          ids: params.departmentVisibilitySettings.ids,
        },
      }),
    };

    try {
      return await userService.adminUpdateMember(input);
    } catch (error) {
      return rejectWithValue(serializeError(error as Error));
    }
  }
);

const usersAdapter = createEntityAdapter<PaidolUser>();

function removeDuplicates(users: PaidolUser[]): PaidolUser[] {
  const uniqueUsers = new Map<string, PaidolUser>();

  users.forEach((user) => {
    if (user.user_id && !uniqueUsers.has(user.user_id)) {
      uniqueUsers.set(user.user_id, user);
    }
  });

  return Array.from(uniqueUsers.values());
}

const initialState = usersAdapter.getInitialState({
  loading: false,
  searchText: '',
  nextToken: null,
  pictureUrls: {} as Record<string, string>,
  panelOpen: false,
  users: [] as Array<PaidolUser>,
  groupAdmins: [] as PaidolUser[],
  groupUsers: [] as Array<PaidolUser>,
  allUsers: [] as Array<PaidolUser>,
});

export type TeamUsersState = typeof initialState;

const usersSlice = createSlice({
  name: 'team/users',
  initialState,
  reducers: {
    // SEE-1846: keep the list in step after an admin changes a member's phone number, so
    // reopening their edit screen shows the new one.
    setMemberPhoneNumber: (state, action: PayloadAction<{ paidolUserId: string; phoneNumber: string }>) => {
      const member = state.entities[action.payload.paidolUserId];
      if (member?.user) member.user.phone_number = action.payload.phoneNumber;
    },
    setCompaniesSearchText: (state, action) => {
      state.searchText = action.payload;
      state.nextToken = null;
    },
    setNextToken: (state, action) => {
      state.nextToken = action.payload;
    },
    resetNextToken: (state) => {
      state.nextToken = null;
    },
    clearUsers: () => {
      return initialState;
    },
    resetUsersSlice: () => {
      return initialState;
    },
    setPanelOpen: (state, action) => {
      state.panelOpen = action.payload;
    },

    setGroupAdmins: (state, action) => {
      state.groupAdmins = action.payload;
    },
  },
  extraReducers: (builder) => {
    withLoading(getUsers, builder, {
      afterFulfilled: (state, action) => {
        if (action.payload) {
          const hasFilter = action.meta.arg.filter !== undefined;
          const hasNextToken = action.meta.arg.nextToken !== null;

          if (hasFilter && !hasNextToken) {
            usersAdapter.setAll(state, action.payload);
          } else {
            usersAdapter.addMany(state, action.payload);
          }
        }
      },
    });

    builder.addCase(getGroupUsers.fulfilled, (state, action) => {
      const combinedUsers = [...state.groupAdmins, ...(action.payload || [])];
      state.groupUsers = removeDuplicates(combinedUsers);
    });

    builder.addCase(getAdmins.fulfilled, (state, action) => {
      if (action.payload) {
        state.groupAdmins = action.payload;
      }
    });

    builder.addCase(getUsersPictures.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.forEach(([user_id, url]) => {
          if (isNotNullOrUndefined(user_id) && isNotNullOrUndefined(url)) {
            state.pictureUrls[user_id] = url;
          }
        });
      }
    });

    builder.addCase(addUser.fulfilled, () => {
      // No optimistic insert — the caller's onUserAdded() callback triggers a
      // full refetch that provides the complete PaidolUser shape with nested fields.
    });

    builder.addCase(removeUser.fulfilled, (state, action) => {
      if (action.payload) {
        usersAdapter.removeOne(state, action.payload);
        state.groupAdmins = state.groupAdmins.filter((admin) => admin.id !== action.payload);
      }
    });

    builder.addCase(updateUser.fulfilled, (state, action) => {
      // SEE-856 D1: adminUpdateMember returns the updated PaidolUser dict
      // directly (parsed from BasicHTTPResponse.body), not wrapped in a
      // GraphQLResult.data.updatePaidolUser shape.
      const updatedUser = action.payload as unknown as PaidolUser | undefined;
      if (updatedUser?.id) {
        usersAdapter.updateOne(state, {
          id: updatedUser.id,
          changes: updatedUser,
        });

        const isAdmin =
          updatedUser.roles === Role.ADMINISTRATOR || updatedUser.roles === Role.GROUP_ADMINISTRATOR;
        const existingAdminIndex = state.groupAdmins.findIndex((admin) => admin.id === updatedUser.id);

        if (isAdmin && existingAdminIndex === -1) {
          state.groupAdmins.push(updatedUser);
        } else if (!isAdmin && existingAdminIndex !== -1) {
          state.groupAdmins.splice(existingAdminIndex, 1);
        } else if (isAdmin && existingAdminIndex !== -1) {
          state.groupAdmins[existingAdminIndex] = updatedUser;
        }
      }
    });

    builder.addCase(getAllUsersNoLimit.fulfilled, (state, action) => {
      state.allUsers = action.payload || [];
    });
  },
});

export const {
  setGroupAdmins,
  setMemberPhoneNumber,
  setCompaniesSearchText,
  setNextToken,
  resetNextToken,
  clearUsers,
  resetUsersSlice,
  setPanelOpen,
} = usersSlice.actions;

export const selectUsersSlice = (state: RootState): TeamUsersState => state.cards?.users ?? initialState;

export const { selectAll: selectUsers, selectById: selectProductById } =
  usersAdapter.getSelectors(selectUsersSlice);

export default usersSlice.reducer;
